<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  ACTIVE_STATUSES,
  useZuwadAssistantStore,
} from 'dashboard/stores/zuwadAssistant';
import { activityFor, renderAnswer } from '../assistantFormat';

const props = defineProps({
  message: { type: Object, required: true },
});
const emit = defineEmits(['approve', 'deny']);

const { t, te } = useI18n();
const store = useZuwadAssistantStore();
// In a shared chat, questions from other people show who asked.
const isMyQuestion = computed(
  () => !props.message.user || props.message.user.id === store.me
);
const askerName = computed(() =>
  props.message.user && props.message.user.id !== store.me
    ? props.message.user.name
    : ''
);
const showSteps = ref(false);

const isUser = computed(() => props.message.role === 'user');
const isActive = computed(() => ACTIVE_STATUSES.includes(props.message.status));
const html = computed(() => renderAnswer(props.message.content));
const steps = computed(() => props.message.tool_events || []);
// Show exactly what would run: the reason and the full command, every space and line kept.
const approvalReason = computed(
  () => props.message.approval?.description || ''
);
const approvalCommand = computed(() => props.message.approval?.command || '');
const runningStep = computed(() =>
  [...steps.value].reverse().find(step => step.status === 'running')
);
// While an answer runs, say what is happening and for how long, so nobody just waits.
const activity = computed(() => {
  if (runningStep.value)
    return t(`ZUWAD_ASSISTANT.ACTIVITY.${activityFor(runningStep.value)}`);
  return props.message.content
    ? t('ZUWAD_ASSISTANT.ACTIVITY.WRITING')
    : t('ZUWAD_ASSISTANT.ACTIVITY.THINKING');
});
const now = ref(Date.now());
let timer = null;
watch(
  isActive,
  active => {
    clearInterval(timer);
    timer = active
      ? setInterval(() => {
          now.value = Date.now();
        }, 1000)
      : null;
  },
  { immediate: true }
);
onBeforeUnmount(() => clearInterval(timer));
const elapsed = computed(() => {
  const started = Date.parse(props.message.created_at);
  if (Number.isNaN(started)) return '';
  const seconds = Math.max(0, Math.round((now.value - started) / 1000));
  return seconds < 60
    ? `${seconds}s`
    : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
});
// A quote in an answer is a ready message for the customer: one click copies it.
const copyQuote = async event => {
  const quote = event.target.closest?.('blockquote');
  if (!quote) return;
  await copyTextToClipboard(quote.innerText.trim());
  useAlert(t('ZUWAD_ASSISTANT.MESSAGE_COPIED'));
};
const copy = async () => {
  await copyTextToClipboard(props.message.content);
  useAlert(t('ZUWAD_ASSISTANT.COPIED'));
};
const errorText = computed(() => {
  const code = props.message.error;
  if (!code) return '';
  const key = `ZUWAD_ASSISTANT.ERRORS.${code}`;
  return te(key) ? t(key) : t('ZUWAD_ASSISTANT.ERRORS.failed');
});
</script>

<template>
  <div
    class="group/message flex w-full"
    :class="isUser ? 'flex-col items-end' : 'flex-col gap-1.5'"
  >
    <div v-if="!isUser" class="flex items-center gap-2 flex-wrap">
      <span
        class="flex items-center justify-center rounded-lg size-7 bg-n-brand text-n-gold shadow-sm"
      >
        <span class="i-woot-zuwad-ai size-4" />
      </span>
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('ZUWAD_ASSISTANT.TITLE') }}
      </span>
      <button
        v-if="steps.length"
        type="button"
        class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs bg-n-alpha-1 text-n-slate-11 hover:text-n-slate-12"
        @click="showSteps = !showSteps"
      >
        <span class="i-lucide-list-checks" />
        <span>{{
          t('ZUWAD_ASSISTANT.STEPS', { count: steps.length }, steps.length)
        }}</span>
        <span
          :class="showSteps ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        />
      </button>
    </div>
    <span
      v-if="isUser && askerName"
      dir="auto"
      class="self-end mb-1 text-xs text-n-slate-11"
      >{{ askerName }}</span
    >
    <div
      class="text-n-slate-12"
      :class="
        isUser
          ? 'max-w-[80%] rounded-2xl rounded-se-md px-4 py-2.5 text-[15px] leading-6 bg-n-solid-blue'
          : 'w-full min-w-0 ps-9 text-[15px] leading-7'
      "
    >
      <div
        v-if="isUser && message.images?.length"
        class="flex flex-wrap gap-2 mb-2"
      >
        <a
          v-for="image in message.images"
          :key="image.id"
          :href="image.url"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            :src="image.url"
            :alt="image.filename"
            class="object-cover rounded-lg size-24 border border-n-weak"
          />
        </a>
      </div>
      <p v-if="isUser" dir="auto" class="whitespace-pre-wrap break-words">
        {{ message.content }}
      </p>

      <template v-else>
        <ul
          v-if="showSteps"
          class="mb-2 space-y-1 text-xs text-n-slate-11 border-s-2 border-n-weak ps-2"
        >
          <li
            v-for="(step, index) in steps"
            :key="index"
            dir="auto"
            class="break-all"
          >
            <span
              :class="{
                'i-lucide-check text-n-teal-10': step.status === 'done',
                'i-lucide-x text-n-ruby-10': step.status === 'error',
                'i-lucide-loader-circle animate-spin':
                  step.status === 'running',
              }"
            />
            <strong>{{ step.tool }}</strong>
            <span v-if="step.preview">{{ ` — ${step.preview}` }}</span>
          </li>
        </ul>

        <p
          v-if="isActive && message.status !== 'waiting_for_approval'"
          class="flex items-center gap-2 mb-2 text-sm text-n-slate-11"
          aria-live="polite"
        >
          <span class="i-lucide-loader-circle animate-spin text-n-brand" />
          <span>{{ activity }}</span>
          <span class="text-xs tabular-nums text-n-slate-10">{{
            elapsed
          }}</span>
        </p>
        <div
          v-if="message.content"
          v-dompurify-html="html"
          dir="auto"
          class="prose prose-bubble max-w-none break-words zuwad-assistant-answer"
          :style="{
            '--zuwad-copy-label': JSON.stringify(
              t('ZUWAD_ASSISTANT.COPY_MESSAGE')
            ),
          }"
          @click="copyQuote"
        />

        <div
          v-if="message.status === 'waiting_for_approval' && message.approval"
          class="p-3 mt-2 rounded-lg bg-n-amber-2 border border-n-amber-6"
        >
          <p class="mb-2 text-xs">
            {{ t('ZUWAD_ASSISTANT.WAITING_APPROVAL') }}
          </p>
          <p v-if="approvalReason" dir="auto" class="mb-1 text-xs font-medium">
            {{ approvalReason }}
          </p>
          <code
            v-if="approvalCommand"
            dir="ltr"
            class="block max-h-60 p-2 mb-2 overflow-auto text-xs rounded bg-n-solid-1 whitespace-pre-wrap break-all"
            >{{ approvalCommand }}</code
          >
          <p v-if="!isMyQuestion" class="text-xs text-n-slate-11">
            {{
              t('ZUWAD_ASSISTANT.APPROVAL_BY', {
                name: message.user?.name || '',
              })
            }}
          </p>
          <div v-else class="flex gap-2">
            <Button
              size="sm"
              color="amber"
              :label="t('ZUWAD_ASSISTANT.APPROVE')"
              @click="emit('approve')"
            />
            <Button
              size="sm"
              variant="outline"
              color="slate"
              :label="t('ZUWAD_ASSISTANT.DENY')"
              @click="emit('deny')"
            />
          </div>
        </div>

        <div
          v-if="message.content && !isActive"
          class="flex mt-1 opacity-0 group-hover/message:opacity-100 focus-within:opacity-100"
        >
          <Button
            icon="i-lucide-copy"
            variant="ghost"
            color="slate"
            size="xs"
            :aria-label="t('ZUWAD_ASSISTANT.COPY')"
            :title="t('ZUWAD_ASSISTANT.COPY')"
            @click="copy"
          />
        </div>

        <p
          v-if="message.status === 'cancelled'"
          class="mt-1 text-xs text-n-slate-10"
        >
          {{ t('ZUWAD_ASSISTANT.STOPPED') }}
        </p>
        <p
          v-if="message.status === 'failed'"
          class="mt-1 text-xs text-n-ruby-11"
        >
          {{ errorText }}
        </p>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* Wide answers (tables, code) scroll inside the answer instead of stretching the chat. */
.zuwad-assistant-answer {
  @apply text-[15px] leading-7;
}
.zuwad-assistant-answer :deep(p),
.zuwad-assistant-answer :deep(ul),
.zuwad-assistant-answer :deep(ol) {
  @apply my-2;
}
.zuwad-assistant-answer :deep(strong) {
  @apply font-semibold text-n-slate-12;
}
.zuwad-assistant-answer :deep(table) {
  @apply block w-fit max-w-full overflow-x-auto my-3 text-sm border-separate border-spacing-0 rounded-xl border border-n-weak;
}
.zuwad-assistant-answer :deep(th),
.zuwad-assistant-answer :deep(td) {
  @apply px-3 py-2 text-start border-b border-n-weak;
}
.zuwad-assistant-answer :deep(tr:last-child td) {
  @apply border-b-0;
}
.zuwad-assistant-answer :deep(th) {
  @apply bg-n-alpha-1 font-semibold text-n-slate-12;
}
/* Quotes are ready-to-send messages: a card with the Zuwad gold edge, copied on click. */
.zuwad-assistant-answer :deep(blockquote) {
  @apply relative my-3 rounded-xl border-0 border-s-4 border-solid border-n-gold bg-n-alpha-1 px-4 pt-3 pb-7 font-normal not-italic text-n-slate-12 cursor-pointer transition-colors;
  quotes: none;
}
.zuwad-assistant-answer :deep(blockquote:hover) {
  @apply bg-n-alpha-2;
}
.zuwad-assistant-answer :deep(blockquote p) {
  @apply my-0;
}
.zuwad-assistant-answer :deep(blockquote p::before),
.zuwad-assistant-answer :deep(blockquote p::after) {
  content: none;
}
.zuwad-assistant-answer :deep(blockquote::after) {
  @apply absolute bottom-1.5 end-3 text-xs font-medium text-n-blue-11;
  content: var(--zuwad-copy-label);
}
.zuwad-assistant-answer :deep(pre) {
  @apply overflow-x-auto p-3 rounded-xl bg-n-alpha-1 text-n-slate-12 text-xs;
  direction: ltr;
}
.zuwad-assistant-answer :deep(code) {
  @apply text-n-slate-12;
}
</style>
